import { computed, effect, Injectable, signal } from '@angular/core';
import { LocalStorageKeys } from '@shared/constants/local-storage-keys';
import {
  KnownPoiTypes,
  PoiTypeDefinition,
  PoiTypeGroup,
  WarningPoiSeverities,
} from '../constants/poi-types';
import { POI } from '../interfaces/map-data';

@Injectable({ providedIn: 'root' })
export class PoiVisibilityService {
  private readonly localStorageKey = LocalStorageKeys.HiddenPoiTypes;

  private readonly hiddenTypeKeys = signal<string[]>(this.load());
  // types met on the map, override KnownPoiTypes
  private readonly metTypes = signal<Map<string, PoiTypeDefinition>>(new Map());

  readonly hiddenKeys = computed(() => new Set(this.hiddenTypeKeys()));
  readonly types = computed<PoiTypeDefinition[]>(() => {
    const types = new Map(KnownPoiTypes.map((type) => [type.key, type]));
    this.metTypes().forEach((type, key) => types.set(key, type));
    return [...types.values()];
  });
  readonly shownCount = computed(
    () => this.types().filter((type) => !this.hiddenKeys().has(type.key)).length,
  );
  readonly hasHiddenTypes = computed(() => this.shownCount() < this.types().length);

  constructor() {
    effect(() => {
      try {
        localStorage.setItem(this.localStorageKey, JSON.stringify(this.hiddenTypeKeys()));
      } catch {
        // storage blocked
      }
    });
  }

  static getTypeKey(poi: POI): string | undefined {
    return poi.POIType_TranslationKey || poi.POITypeName || undefined;
  }

  isPoiShown(poi: POI): boolean {
    const key = PoiVisibilityService.getTypeKey(poi);
    return !key || !this.hiddenKeys().has(key);
  }

  setTypesShown(keys: string[], isShown: boolean): void {
    const hidden = new Set(this.hiddenTypeKeys());
    keys.forEach((key) => (isShown ? hidden.delete(key) : hidden.add(key)));
    this.hiddenTypeKeys.set([...hidden]);
  }

  addMetTypes(pois: POI[]): void {
    const metTypes = new Map(this.metTypes());
    let hasChanges = false;

    pois.forEach((poi) => {
      const key = PoiVisibilityService.getTypeKey(poi);
      if (!key) return;

      const type: PoiTypeDefinition = {
        key,
        iconKey: poi.IconKey || 'icon-warning',
        iconColor: poi.IconColor || 'red',
        group: WarningPoiSeverities.includes(poi.SeverityName ?? '')
          ? PoiTypeGroup.Warnings
          : PoiTypeGroup.Information,
      };
      const existing = metTypes.get(key);
      if (
        existing?.iconKey !== type.iconKey ||
        existing.iconColor !== type.iconColor ||
        existing.group !== type.group
      ) {
        metTypes.set(key, type);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.metTypes.set(metTypes);
    }
  }

  private load(): string[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.localStorageKey) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((key) => typeof key === 'string') : [];
    } catch {
      return [];
    }
  }
}
